import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Plus, CheckCircle, Clock, Euro } from 'lucide-react';
import { toast } from 'sonner';

const VehicleDamageManager = ({ vehicleId, licensePlate, isOpen, onClose }) => {
  const [damages, setDamages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingDamage, setAddingDamage] = useState(false);
  const [newDamage, setNewDamage] = useState({
    position: '',
    description: '',
    severity: 'low',
    notes: ''
  });

  useEffect(() => {
    if (isOpen && vehicleId) {
      loadDamages();
    }
  }, [isOpen, vehicleId]);

  const loadDamages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/vehicles/${vehicleId}/damages`);
      setDamages(res.data.damages || []);
    } catch (error) {
      console.error('Error loading damages:', error);
      toast.error('Fehler beim Laden der Schäden');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDamage = async () => {
    if (!newDamage.position || !newDamage.description) {
      toast.error('Bitte Position und Beschreibung ausfüllen');
      return;
    }

    try {
      await axios.post(`/vehicles/${vehicleId}/damages`, newDamage);
      toast.success('Schaden erfolgreich dokumentiert');
      setNewDamage({
        position: '',
        description: '',
        severity: 'low',
        notes: ''
      });
      setAddingDamage(false);
      loadDamages();
    } catch (error) {
      console.error('Error adding damage:', error);
      toast.error('Fehler beim Hinzufügen des Schadens');
    }
  };

  const handleMarkRepaired = async (damageId) => {
    const cost = prompt('Reparaturkosten in € (optional):');
    const notes = prompt('Notizen zur Reparatur (optional):');

    try {
      const updateData = { status: 'repaired' };
      if (cost && !isNaN(parseFloat(cost))) {
        updateData.repair_cost = parseFloat(cost);
      }
      if (notes) {
        updateData.notes = notes;
      }

      await axios.patch(`/damages/${damageId}`, updateData);
      toast.success('Schaden als repariert markiert');
      loadDamages();
    } catch (error) {
      console.error('Error updating damage:', error);
      toast.error('Fehler beim Aktualisieren des Schadens');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'low': return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50';
      case 'medium': return 'bg-orange-500/20 text-orange-300 border-orange-500/50';
      case 'high': return 'bg-red-500/20 text-red-300 border-red-500/50';
      default: return 'bg-gray-500/20 text-gray-300 border-gray-500/50';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'existing': return 'bg-blue-500/20 text-blue-300 border-blue-500/50';
      case 'new': return 'bg-red-500/20 text-red-300 border-red-500/50';
      case 'repaired': return 'bg-green-500/20 text-green-300 border-green-500/50';
      default: return 'bg-gray-500/20 text-gray-300 border-gray-500/50';
    }
  };

  const getPositionLabel = (position) => {
    const labels = {
      'front': 'Vorne',
      'rear': 'Hinten',
      'left': 'Links',
      'right': 'Rechts',
      'top': 'Dach',
      'interior': 'Innenraum'
    };
    return labels[position] || position;
  };

  const getSeverityLabel = (severity) => {
    const labels = {
      'low': 'Gering',
      'medium': 'Mittel',
      'high': 'Hoch'
    };
    return labels[severity] || severity;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'existing': 'Bestandsschaden',
      'new': 'Neu gemeldet',
      'repaired': 'Repariert'
    };
    return labels[status] || status;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[#0a0a0a] text-white border border-gray-800">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-orange-500" />
            Schadensregister: {licensePlate}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Dokumentation aller Schäden für dieses Fahrzeug
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Add New Damage Section */}
          <Card className="bg-[#111] border-gray-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                <span>Neuen Schaden hinzufügen</span>
                <Button
                  onClick={() => setAddingDamage(!addingDamage)}
                  variant="outline"
                  size="sm"
                  className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {addingDamage ? 'Abbrechen' : 'Schaden hinzufügen'}
                </Button>
              </CardTitle>
            </CardHeader>
            {addingDamage && (
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Position *</Label>
                    <Select
                      value={newDamage.position}
                      onValueChange={(value) => setNewDamage({ ...newDamage, position: value })}
                    >
                      <SelectTrigger className="bg-[#1a1a1a] border-gray-700">
                        <SelectValue placeholder="Position wählen" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-gray-700">
                        <SelectItem value="front">Vorne</SelectItem>
                        <SelectItem value="rear">Hinten</SelectItem>
                        <SelectItem value="left">Links</SelectItem>
                        <SelectItem value="right">Rechts</SelectItem>
                        <SelectItem value="top">Dach</SelectItem>
                        <SelectItem value="interior">Innenraum</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Schweregrad *</Label>
                    <Select
                      value={newDamage.severity}
                      onValueChange={(value) => setNewDamage({ ...newDamage, severity: value })}
                    >
                      <SelectTrigger className="bg-[#1a1a1a] border-gray-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-gray-700">
                        <SelectItem value="low">Gering</SelectItem>
                        <SelectItem value="medium">Mittel</SelectItem>
                        <SelectItem value="high">Hoch</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Beschreibung *</Label>
                  <Textarea
                    value={newDamage.description}
                    onChange={(e) => setNewDamage({ ...newDamage, description: e.target.value })}
                    placeholder="Detaillierte Beschreibung des Schadens..."
                    className="bg-[#1a1a1a] border-gray-700 min-h-[80px]"
                  />
                </div>
                <div>
                  <Label>Notizen (optional)</Label>
                  <Textarea
                    value={newDamage.notes}
                    onChange={(e) => setNewDamage({ ...newDamage, notes: e.target.value })}
                    placeholder="Zusätzliche Informationen..."
                    className="bg-[#1a1a1a] border-gray-700 min-h-[60px]"
                  />
                </div>
                <Button
                  onClick={handleAddDamage}
                  className="w-full bg-orange-600 hover:bg-orange-700"
                >
                  Schaden dokumentieren
                </Button>
              </CardContent>
            )}
          </Card>

          {/* Damages List */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold">Alle Schäden ({damages.length})</h3>
            {loading ? (
              <div className="text-center py-8 text-gray-400">Lade Schäden...</div>
            ) : damages.length === 0 ? (
              <Card className="bg-[#111] border-gray-800">
                <CardContent className="py-8 text-center text-gray-400">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500" />
                  <p>Keine Schäden dokumentiert</p>
                  <p className="text-sm mt-1">Dieses Fahrzeug ist in einwandfreiem Zustand</p>
                </CardContent>
              </Card>
            ) : (
              damages.map((damage) => (
                <Card key={damage.damage_id} className="bg-[#111] border-gray-800 hover:border-gray-700 transition-colors">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={getStatusColor(damage.status)}>
                            {getStatusLabel(damage.status)}
                          </Badge>
                          <Badge className={getSeverityColor(damage.severity)}>
                            {getSeverityLabel(damage.severity)}
                          </Badge>
                          <Badge variant="outline" className="border-gray-600 text-gray-300">
                            {getPositionLabel(damage.position)}
                          </Badge>
                        </div>
                        <p className="text-base font-medium">{damage.description}</p>
                        <div className="text-sm text-gray-400 space-y-1">
                          <p>
                            Gemeldet von: <span className="text-gray-300">{damage.reported_by_name}</span>
                          </p>
                          <p>
                            Datum: <span className="text-gray-300">
                              {new Date(damage.reported_at).toLocaleDateString('de-DE', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </p>
                          {damage.notes && (
                            <p>
                              Notizen: <span className="text-gray-300">{damage.notes}</span>
                            </p>
                          )}
                          {damage.repair_cost && (
                            <p className="flex items-center gap-1">
                              <Euro className="h-4 w-4" />
                              Reparaturkosten: <span className="text-gray-300">{damage.repair_cost.toFixed(2)} €</span>
                            </p>
                          )}
                          {damage.repaired_at && (
                            <p>
                              Repariert am: <span className="text-gray-300">
                                {new Date(damage.repaired_at).toLocaleDateString('de-DE')}
                              </span>
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="ml-4">
                        {damage.status !== 'repaired' && (
                          <Button
                            onClick={() => handleMarkRepaired(damage.damage_id)}
                            variant="outline"
                            size="sm"
                            className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Repariert
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VehicleDamageManager;
